import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import SeriesAutocomplete from '../Cricket/SeriesAutocomplete';
import MatchAutocomplete from '../Cricket/MatchAutocomplete';
import PlayerAutocomplete from '../Cricket/PlayerAutocomplete';

interface TaggedItem {
  id?: number;
  tag_type: string;
  content_type: string;
  object_id: number;
  content_object?: any;
}

interface ArticleTagsProps {
  value: TaggedItem[];
  onChange: (tags: TaggedItem[]) => void;
  disabled?: boolean;
}

const TAG_TYPE_OPTIONS = {
  match: ['preview', 'news'],
  series: ['preview', 'news'],
  player: ['profile', 'news'],
};

const ArticleTags: React.FC<ArticleTagsProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [selectedType, setSelectedType] = useState<'series' | 'match' | 'player'>('match');
  const [selectedTagType, setSelectedTagType] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const handleAddTag = () => {
    if (!selectedItem || !selectedTagType) return;

    const newTag: TaggedItem = {
      tag_type: selectedTagType,
      content_type: selectedType,
      object_id: selectedItem.id,
      content_object: selectedItem,
    };

    onChange([...value, newTag]);
    setSelectedTagType('');
    setSelectedItem(null);
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...value];
    newTags.splice(index, 1);
    onChange(newTags);
  };

  const getTagLabel = (tag: TaggedItem) => {
    if (!tag.content_object) return 'Unknown';
    
    switch (tag.content_type) {
      case 'match':
      case 'series':
      case 'player':
        return tag.content_object.name;
      default:
        return 'Unknown';
    }
  };

  const canAddTag = selectedItem && selectedTagType;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Tags
      </Typography>

      {!disabled && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <Grid container>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Content Type"
                    onChange={(e) => setSelectedType(e.target.value as 'series' | 'match' | 'player')}
                  >
                    <MenuItem value="match">Match</MenuItem>
                    <MenuItem value="series">Series</MenuItem>
                    <MenuItem value="player">Player</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Tag Type</InputLabel>
                  <Select
                    value={selectedTagType}
                    label="Tag Type"
                    onChange={(e) => setSelectedTagType(e.target.value)}
                  >
                    {TAG_TYPE_OPTIONS[selectedType].map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {selectedType === 'match' && (
              <MatchAutocomplete
                value={selectedItem}
                onChange={setSelectedItem}
                disabled={!selectedTagType}
              />
            )}
            {selectedType === 'series' && (
              <SeriesAutocomplete
                value={selectedItem}
                onChange={setSelectedItem}
                disabled={!selectedTagType}
              />
            )}
            {selectedType === 'player' && (
              <PlayerAutocomplete
                value={selectedItem}
                onChange={setSelectedItem}
                disabled={!selectedTagType}
              />
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddTag}
              disabled={!canAddTag}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Tag
            </Button>
          </Stack>
        </Paper>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {value.map((tag, index) => (
          <Chip
            key={index}
            label={`${getTagLabel(tag)} (${tag.tag_type})`}
            onDelete={disabled ? undefined : () => handleRemoveTag(index)}
            deleteIcon={disabled ? undefined : <DeleteIcon />}
            sx={{ m: 0.5 }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ArticleTags; 